import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Scale, Server, FileText, AlertTriangle, Mail, MapPin } from 'lucide-react';

export const MentionsLegales = () => {
  return (
    <>
      <Helmet>
        <title>Mentions Légales | CAMBC Conseils Moissac</title>
        <meta name="description" content="Mentions légales Cabinet CAMBC Conseils. ORIAS 24000879, SIREN 882747652. Alexandre MEUX, courtier assurance Moissac." />
        <meta name="keywords" content="mentions légales, CAMBC Conseils, ORIAS 24000879, SIREN 882747652, Alexandre MEUX" />
        <link rel="canonical" href="https://www.cambc-conseils.fr/mentions-legales" />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-blue-700 rounded-full flex items-center justify-center">
              <Scale className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Mentions Légales</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Informations légales et réglementaires
          </p>
        </div>

        <div className="space-y-8">
          {/* Informations sur l'entreprise */}
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Informations sur l'entreprise</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p><strong>Raison sociale :</strong> Cabinet CAMBC Conseils</p>
                <p><strong>Dirigeant :</strong> Alexandre MEUX</p>
                <p><strong>Adresse :</strong> 46 boulevard Pierre Delbrel, 82200 Moissac</p>
                <p><strong>Téléphone :</strong> 05 82 81 91 20</p>
                <p><strong>Email :</strong> cabinet@cambc-conseils.fr</p>
                <p><strong>Activité :</strong> Courtage en assurance</p>
                <p><strong>SIREN :</strong> 882 747 652</p>
                
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mt-6">
                  <p className="font-semibold text-blue-900">
                    <strong>Numéro ORIAS :</strong> 24000879
                  </p>
                  <p className="text-sm text-blue-700 mt-2">
                    Vérifiable sur le site <a href="https://www.orias.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">www.orias.fr</a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Autorité de contrôle - ACPR */}
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Autorité de contrôle</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p>
                  Notre activité de courtage en assurance est soumise au contrôle de l'<strong>Autorité de Contrôle Prudentiel et de Résolution (ACPR)</strong>.
                </p>
                
                <div className="bg-green-50 border-l-4 border-green-600 p-4">
                  <p className="font-semibold text-green-900">ACPR</p>
                  <p className="text-green-700">4 Place de Budapest</p>
                  <p className="text-green-700">CS 92459</p>
                  <p className="text-green-700">75436 Paris Cedex 09</p>
                  <p className="text-green-700 mt-2">
                    Site internet : <a href="https://acpr.banque-france.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">acpr.banque-france.fr</a>
                  </p>
                </div>
                
                <p className="text-sm text-gray-600">
                  En tant qu'intermédiaire en assurance, nous sommes immatriculés au Registre des Intermédiaires en Assurance (ORIAS) et soumis aux dispositions du Code des assurances.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Hébergement du site */}
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Server className="w-6 h-6 text-purple-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Hébergement du site</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p>Ce site est hébergé par :</p>
                
                <div className="bg-purple-50 border-l-4 border-purple-600 p-4">
                  <p className="font-semibold text-purple-900">Emergent Labs</p>
                  <p className="text-purple-700">Plateforme d'hébergement cloud sécurisée</p>
                  <p className="text-purple-700 mt-2">
                    Site internet : <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="underline hover:text-purple-900">emergent.sh</a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Protection des données */}
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-orange-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Protection des données personnelles</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <h3 className="font-semibold text-lg text-gray-900">Responsable du traitement</h3>
                <p>
                  Le responsable du traitement des données personnelles collectées sur ce site est le Cabinet CAMBC Conseils, situé au 46 boulevard Pierre Delbrel, 82200 Moissac.
                </p>
                
                <h3 className="font-semibold text-lg text-gray-900 mt-6">Données collectées</h3>
                <p>
                  Dans le cadre de nos services, nous collectons les données suivantes :
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Nom et prénom</li>
                  <li>Adresse email</li>
                  <li>Numéro de téléphone</li>
                  <li>Adresse postale</li>
                  <li>Documents d'identité (permis de conduire, carte grise) dans le cadre des demandes d'assurance temporaire</li>
                  <li>Informations relatives à votre situation d'assurance</li>
                </ul>
                
                <h3 className="font-semibold text-lg text-gray-900 mt-6">Finalités du traitement</h3>
                <p>Vos données personnelles sont collectées pour les finalités suivantes :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Répondre à vos demandes de contact et de devis</li>
                  <li>Établir des propositions d'assurance personnalisées</li>
                  <li>Gérer la relation client et le suivi de vos contrats</li>
                  <li>Respecter nos obligations légales et réglementaires</li>
                </ul>
                
                <h3 className="font-semibold text-lg text-gray-900 mt-6">Base légale</h3>
                <p>
                  Le traitement de vos données est fondé sur :
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Votre consentement lors de la soumission des formulaires</li>
                  <li>L'exécution de mesures précontractuelles ou contractuelles</li>
                  <li>Le respect de nos obligations légales</li>
                </ul>
                
                <h3 className="font-semibold text-lg text-gray-900 mt-6">Durée de conservation</h3>
                <p>
                  Vos données personnelles sont conservées pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées, et conformément aux obligations légales de conservation applicables au secteur de l'assurance.
                </p>
                
                <h3 className="font-semibold text-lg text-gray-900 mt-6">Vos droits</h3>
                <p>
                  Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Droit d'accès :</strong> obtenir la confirmation que vos données sont traitées et en obtenir une copie</li>
                  <li><strong>Droit de rectification :</strong> demander la correction de données inexactes</li>
                  <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données dans les cas prévus par la loi</li>
                  <li><strong>Droit à la limitation :</strong> demander la limitation du traitement de vos données</li>
                  <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                  <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                </ul>
                
                <div className="bg-orange-50 border-l-4 border-orange-600 p-4 mt-6">
                  <p className="font-semibold text-orange-900">Pour exercer vos droits :</p>
                  <p className="text-orange-700">Adressez votre demande par email à : <a href="mailto:cabinet@cambc-conseils.fr" className="underline">cabinet@cambc-conseils.fr</a></p>
                  <p className="text-orange-700">Ou par courrier à : Cabinet CAMBC Conseils, 46 boulevard Pierre Delbrel, 82200 Moissac</p>
                </div>
                
                <p className="mt-4 text-sm text-gray-600">
                  Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800">www.cnil.fr</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Réclamations et Médiation */}
          <Card className="shadow-lg border-0 border-t-4 border-t-red-500">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Réclamations et Médiation</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p>
                  Si vous n'êtes pas parvenu à résoudre votre litige après nous avoir adressé une réclamation écrite (courrier ou courriel), datée, rappelant les circonstances qui ont donné lieu au différend et ce que vous réclamez, vous pourrez saisir le médiateur de la consommation - désigné ci-dessous - si vous avez reçu une réponse écrite négative de notre part ou si vous n'avez pas reçu de réponse deux mois après l'envoi de votre réclamation.
                </p>
                
                <p>
                  Conformément aux articles L.616-1 et R.616-1 du code de la consommation, notre société a mis en place un dispositif de médiation de la consommation.
                </p>
                
                <div className="bg-red-50 border-l-4 border-red-600 p-6 mt-6">
                  <h3 className="font-bold text-red-900 text-lg mb-4">L'entité de médiation retenue est :</h3>
                  
                  <p className="font-semibold text-red-900 text-lg">
                    MÉDIATION CONSOMMATION DÉVELOPPEMENT / MED CONSO DEV
                  </p>
                  
                  <div className="mt-4 space-y-2">
                    <p className="flex items-start gap-2">
                      <Mail className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
                      <span>
                        En cas de litige, vous pouvez déposer votre réclamation sur son site : <a href="https://www.medconsodev.eu" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-red-900">www.medconsodev.eu</a>
                      </span>
                    </p>
                    
                    <p className="flex items-start gap-2 mt-4">
                      <MapPin className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
                      <span>
                        Ou par voie postale en écrivant à :
                      </span>
                    </p>
                    
                    <div className="ml-7 mt-2 p-4 bg-white rounded-lg border border-red-200">
                      <p className="font-semibold">MÉDIATION CONSOMMATION DÉVELOPPEMENT</p>
                      <p>C/O Centre d'Affaires Stéphanois SAS</p>
                      <p>IMMEUBLE L'HORIZON – ESPLANADE DE FRANCE</p>
                      <p>3, RUE J. CONSTANT MILLERET</p>
                      <p className="font-semibold">42000 SAINT-ÉTIENNE</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-4 rounded-lg mt-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Pour nous adresser une réclamation :</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Par email : <a href="mailto:cabinet@cambc-conseils.fr" className="text-blue-600 underline">cabinet@cambc-conseils.fr</a></li>
                    <li>Par courrier : Cabinet CAMBC Conseils, 46 boulevard Pierre Delbrel, 82200 Moissac</li>
                    <li>Par téléphone : 05 82 81 91 20</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Propriété intellectuelle */}
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-gray-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Propriété intellectuelle</h2>
              </div>
              
              <div className="space-y-4 text-gray-700">
                <p>
                  L'ensemble du contenu de ce site (textes, images, logos, graphismes) est la propriété exclusive du Cabinet CAMBC Conseils, sauf mention contraire.
                </p>
                <p>
                  Toute reproduction, représentation, modification, publication, transmission ou dénaturation, totale ou partielle du site ou de son contenu, par quelque procédé que ce soit et sur quelque support que ce soit est interdite sans autorisation préalable écrite.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Dernière mise à jour */}
          <div className="text-center text-sm text-gray-500 mt-8">
            <p>Dernière mise à jour : Février 2026</p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
